import { Alert, Button, Card, Modal, Spinner } from "flowbite-react";
import Input from "../inputs/Input";
import SelectInput from "../inputs/SelectInput";
import SurveySection from "./SurveySection";
import { QUERIES, QUERIES_VEICULAR, SURVEYS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { IoSearchSharp } from "react-icons/io5";
import { Tooltip } from "react-tooltip";
import {
  HiClipboardList,
  HiOutlineBadgeCheck,
  HiOutlineExclamationCircle,
} from "react-icons/hi";
import { toast } from "react-toastify";

const surveyVehicleSchema = yup.object().shape({
  vehicle: yup.object().shape({
    plate: yup
      .string()
      .required("Placa é obrigatória")
      .min(7, "Placa deve ter 7 caracteres"),
    renavam: yup.string().nullable().notRequired(),
    km: yup.string().nullable(),
    engine: yup.string().nullable().notRequired(),
    isEngineFactoryStandard: yup.bool().nullable(),
    engineObservation: yup.string().nullable(),
    chassi: yup.string().nullable().notRequired(),
    isChassiFactoryStandard: yup.bool().nullable(),
    chassiObservation: yup.string().nullable(),
    selectedQueryType: yup
      .string()
      .nullable()
      .notRequired()
      .oneOf(["auto-vistoria", "leilao-vip-gold", null]),
  }),
});

const SurveyVehicle = ({
  setActiveStep,
  surveyVehicle,
  setSurveyVehicle,
  survey,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGettingVehicleInfos, setIsGettingVehicleInfos] = useState(false);
  const [openModalSearchPlate, setOpenModalSearchPlate] = useState(false);
  const [error, setError] = useState(null);
  const [errorQuery, setErrorQuery] = useState(null);
  const [skipError, setSkipError] = useState(null);
  const [vehicleQuery, setVehicleQuery] = useState(
    surveyVehicle?.vehicle?.vehicleQuery || null
  );
  const [hasFetchedVehicle, setHasFetchedVehicle] = useState(
    !!surveyVehicle?.vehicle?.vehicleQuery
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(surveyVehicleSchema),
  });

  useEffect(() => {
    if (surveyVehicle) {
      Object.keys(surveyVehicle).forEach((s) => {
        setValue(s, surveyVehicle[s]);
      });

      if (surveyVehicle.vehicle?.vehicleQuery) {
        const resumoCodigo =
          surveyVehicle?.vehicle?.vehicleQuery?.resumoRetorno?.codigoConsulta;

        if (resumoCodigo === 89) {
          setValue("vehicle.selectedQueryType", "leilao-vip-gold");
        } else if (resumoCodigo === 72) {
          setValue("vehicle.selectedQueryType", "auto-vistoria");
        }

        setVehicleQuery(surveyVehicle?.vehicle?.vehicleQuery);
        setHasFetchedVehicle(true);
      }
    }
  }, []);

  const fetchVehicleDataInBackground = async (plate, currentVehicle) => {
    const selectedQuery = watch("vehicle.selectedQueryType");

    if (!selectedQuery) {
      return;
    }
    
    const toastId = toast.loading("Consultando dados do veículo...");

    try {
      const { data: fetchedData } = await axios.get(
        `${QUERIES.GET_V2_QUERY}/consulta-leilao-vip-gold/${plate}`
      );

      const vehicleQuery =
        (fetchedData?.data?.binNacional?.statusRetorno === 1 &&
          fetchedData?.data?.binNacional) ||
        (fetchedData?.data?.binEstadual?.statusRetorno === 1 &&
          fetchedData?.data?.binEstadual);

      if (vehicleQuery) {
        const veiculo = {
          renavam: vehicleQuery.renavam,
          model: vehicleQuery?.marcaModelo,
          fuel: vehicleQuery?.combustivel,
          yearMake: vehicleQuery?.anoFabricacao,
          yearModel: vehicleQuery?.anoModelo,
          engine: vehicleQuery?.numeroMotor,
          chassi: vehicleQuery?.chassi,
          maker: "",
          color: vehicleQuery?.corVeiculo,
          img: vehicleQuery?.urlLogo?.thumb,
          // ❌ km removido
        };

        Object.entries(veiculo).forEach(([key, value]) => {
          setValue(`vehicle.${key}`, value ?? null);
        });

        const updatedVehicle = {
          ...currentVehicle,
          ...veiculo,
          vehicleQuery: fetchedData.data,
        };

        await axios.post(SURVEYS.GET_SURVEY_VEHICLE, {
          surveyId: survey.id,
          vehicle: updatedVehicle,
        });

        setSurveyVehicle({ vehicle: updatedVehicle });
        setHasFetchedVehicle(true);

        toast.update(toastId, {
          render: "Consulta veicular concluída!",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      } else {
        toast.update(toastId, {
          render: "Consulta realizada, mas nenhum dado foi retornado.",
          type: "warning",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.update(toastId, {
        render: "Erro ao buscar dados do veículo.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  useEffect(() => {
    if (vehicleQuery) {
      setValue("vehicle.vehicleQuery", vehicleQuery);
    }
  }, [vehicleQuery, setValue]);

  const getInfoByPlate = async () => {
    const selectedQuery = watch("vehicle.selectedQueryType");

    if (!selectedQuery) {
      return;
    }
    setIsGettingVehicleInfos(true);
    setErrorQuery(null);
    setVehicleQuery(null);
    try {
      const { data } = await axios.get(
        `${QUERIES.GET_V2_QUERY}/consulta-leilao-vip-gold/${watch(
          "vehicle.plate"
        )?.replace("-", "")}`
      );

      const dadosVeiculo =
        (data?.data?.binNacional?.statusRetorno == 1 &&
          data?.data?.binNacional) ||
        (data?.data?.binEstadual?.statusRetorno == 1 &&
          data?.data?.binEstadual);

      const veiculo = {
        renavam: dadosVeiculo.renavam,
        model: dadosVeiculo?.marcaModelo,
        fuel: dadosVeiculo?.combustivel,
        yearMake: dadosVeiculo?.anoFabricacao,
        yearModel: dadosVeiculo?.anoModelo,
        engine: dadosVeiculo?.numeroMotor,
        chassi: dadosVeiculo?.chassi,
        maker: "",
        color: dadosVeiculo?.corVeiculo,
        img: dadosVeiculo?.urlLogo?.thumb,
      };

      Object.keys(veiculo).forEach((key) => {
        setValue(`vehicle[${key}]`, veiculo[key]);
      });
      setVehicleQuery(data.data);
      setOpenModalSearchPlate(false);
    } catch (error) {
      setErrorQuery(
        "Erro ao tentar buscar o veículo por placa. Tente novamente mais tarde!"
      );
    } finally {
      setIsGettingVehicleInfos(false);
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      data.surveyId = survey.id;

      await axios.post(SURVEYS.GET_SURVEY_VEHICLE, data);
      setSurveyVehicle(data);
      setActiveStep(3);

      const plate = data.vehicle.plate?.replace("-", "");
      if (!hasFetchedVehicle && plate?.length === 7) {
        fetchVehicleDataInBackground(plate, data.vehicle);
      }
    } catch (error) {
      toast.error("Erro ao salvar os dados do veículo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoToStep3 = () => {
    const plate = getValues("vehicle.plate")?.replace("-", "");
    const selectedQuery = getValues("vehicle.selectedQueryType");

    if (!plate || plate.length < 7 || !selectedQuery) {
      setSkipError(
        "Você precisa preencher a placa e selecionar a consulta para avançar."
      );
      return;
    }

    setSkipError(null);
    setActiveStep(3);

    if (!hasFetchedVehicle) {
      fetchVehicleDataInBackground(plate, getValues("vehicle"));
    }
  };

  const handlePlateMask = (event) => {
    let value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    value = value.slice(0, 7); // Limita a 7 caracteres
    event.target.value = value;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex justify-start md:justify-end mt-1 mb-10 md:-mb-2">
        <div className="flex-1 md:flex-none flex flex-col md:flex-row gap-2 items-start md:items-center justify-end border border-gray-200 bg-gray-100 rounded-md p-2">
          <div className="text-sm">
            Deseja avançar sem preencher os dados do veículo agora?
          </div>
          <Button
            size="xs"
            onClick={handleGoToStep3}
            isProcessing={isGettingVehicleInfos}
          >
            Avançar
          </Button>
        </div>
      </div>
      {skipError && (
        <small className="text-red-600 mb-10 -mt-8 md:mt-3 block flex justify-end">
          {skipError}
        </small>
      )}
      <div className="mb-8">
        <SurveySection name="Informe a placa do veículo">
          <div className="flex flex-col gap-2 items-center md:items-start">
            <div className="flex w-full max-w-sm overflow-hidden rounded-md shadow border border-blue-700 bg-white">
              {/* Faixa azul lateral */}
              <div className="flex flex-col items-center justify-center bg-blue-700 text-white px-2 py-2 text-xs font-bold w-10">
                <div className="text-[10px]">BR</div>
                <img className="w-5 mt-1" src="/assets/base_nacional.png" />
              </div>

              {/* Campo da placa */}
              <input
                {...register("vehicle.plate")}
                maxLength={7}
                onInput={handlePlateMask}
                placeholder="ABC1234"
                className="w-full text-center text-3xl font-mono tracking-widest uppercase bg-white text-black outline-none px-4 py-2"
              />
            </div>

            {errors.vehicle?.plate && (
              <small className="text-red-600">
                {errors.vehicle.plate.message}
              </small>
            )}
          </div>
        </SurveySection>
      </div>

      <SurveySection name="Selecione a Consulta Desejada">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              label: "Leilão VIP Gold",
              value: "leilao-vip-gold",
              description:
                "Consulta rápida baseada em dados públicos de leilão, sinistros e procedência do veículo.",
              icon: (
                <HiOutlineBadgeCheck size={40} className="text-green-600" />
              ),
              disabled: false,
            },
            {
              label: "Auto Vistoria",
              value: "auto-vistoria",
              description:
                "Consulta mais completa e detalhada, ideal para laudos técnicos com base em inspeção manual e análise minuciosa.",
              icon: <HiClipboardList size={40} className="text-green-600" />,
              disabled: true, // <- adiciona essa flag
            },
          ].map((option) => (
            <label
              key={option.value}
              className={option.disabled ? "cursor-not-allowed" : ""}
            >
              <input
                type="radio"
                {...register("vehicle.selectedQueryType")}
                value={option.value}
                className="hidden peer"
                disabled={option.disabled} // <- desativa o input
              />
              <Card
                className={`h-full flex flex-col justify-between transition-all duration-200 hover:shadow-md border cursor-pointer ${
                  option.disabled
                    ? "bg-gray-100 opacity-60 border-gray-300"
                    : "peer-checked:ring-2 peer-checked:ring-green-600 peer-checked:bg-green-50"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="mt-1">{option.icon}</div>
                  <div className="flex flex-col justify-between h-full">
                    <div>
                      <h4 className="font-bold text-md mb-1">{option.label}</h4>
                      <p className="text-sm text-gray-500">
                        {option.description}
                      </p>
                    </div>
                    {option.disabled && (
                      <p className="text-xs mt-2 text-yellow-600 font-semibold">
                        Disponível em breve
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            </label>
          ))}
        </div>
        {errors.vehicle?.selectedQueryType && (
          <small className="text-red-600 mt-1 block">
            {errors.vehicle.selectedQueryType.message}
          </small>
        )}
      </SurveySection>

      <Alert color="gray" className="mb-4 mt-4" withBorderAccent>
        Ao realizar a consulta, os dados do veículo abaixo serão preenchidos
        automaticamente. Caso prefira, você também pode preenchê-los
        manualmente.
      </Alert>

      <SurveySection name="Dados do veículo">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          <input
            type="hidden"
            {...register("vehicle.vehicleQuery")}
            name="vehicle.vehicleQuery"
          />

          <div>
            <Input register={register} name="vehicle.renavam" label="Renavam" />
            {/* {errors.vehicle?.renavam && (
              <small className="text-red-600">
                {errors.vehicle.renavam.message}
              </small>
            )} */}
          </div>
          <div>
            <Input
              register={register}
              name="vehicle.km"
              label="Quilometragem *"
              type="number"
              step="0.01"
            />
            {errors.vehicle?.km && (
              <small className="text-red-600">
                {errors.vehicle.km.message}
              </small>
            )}
          </div>
          <div>
            <Input
              register={register}
              name="vehicle.model"
              label="Modelo"
              type="text"
            />
          </div>
          <div>
            <Input
              register={register}
              name="vehicle.maker"
              label="Fabricante"
              type="text"
            />
          </div>
          <div>
            <Input
              register={register}
              name="vehicle.fuel"
              label="Combustível"
              type="text"
            />
          </div>
          <div>
            <Input
              register={register}
              name="vehicle.yearMake"
              label="Ano de fabricação"
              type="text"
            />
          </div>
          <div>
            <Input
              register={register}
              name="vehicle.yearModel"
              label="Ano do modelo"
              type="text"
            />
          </div>
          <div>
            <Input
              register={register}
              name="vehicle.color"
              label="Cor"
              type="text"
            />
          </div>
        </div>
      </SurveySection>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SurveySection name="Dados do motor">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Input register={register} name="vehicle.engine" label="Motor" />
              {/* {errors.vehicle?.engine && (
                <small className="text-red-600">
                  {errors.vehicle.engine.message}
                </small>
              )} */}
            </div>
            <div>
              <SelectInput
                register={register}
                name="vehicle.isEngineFactoryStandard"
                label="Padrão Fábrica (Motor)"
              >
                <option value={true}>Sim</option>
                <option value={false}>Não</option>
              </SelectInput>
              {errors.vehicle?.isEngineFactoryStandard && (
                <small className="text-red-600">
                  {errors.vehicle.isEngineFactoryStandard.message}
                </small>
              )}
            </div>
            <Input
              register={register}
              name="vehicle.engineObservation"
              label="Observação Motor"
            />
          </div>
        </SurveySection>

        <SurveySection name="Dados do chassi">
          <input
            {...register("vehicle.img")}
            type="hidden"
            name="vehicle.img"
            label="Logo fabricante"
          />
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Input register={register} name="vehicle.chassi" label="Chassi" />
              {errors.vehicle?.chassi && (
                <small className="text-red-600">
                  {errors.vehicle.chassi.message}
                </small>
              )}
            </div>
            <div>
              <SelectInput
                register={register}
                name="vehicle.isChassiFactoryStandard"
                label="Padrão Fábrica (Chassi)"
              >
                <option value={true}>Sim</option>
                <option value={false}>Não</option>
              </SelectInput>
              {errors.vehicle?.isChassiFactoryStandard && (
                <small className="text-red-600">
                  {errors.vehicle.isChassiFactoryStandard.message}
                </small>
              )}
            </div>
            <Input
              register={register}
              name="vehicle.chassiObservation"
              label="Observação Chassi"
            />
          </div>
        </SurveySection>
      </div>

      <div className="flex justify-center items-center gap-2 mt-10">
        <Button
          type="button"
          onClick={() => setActiveStep(1)}
          className="bg-gray-500 px-5"
          disabled={isSubmitting}
          isProcessing={isSubmitting}
        >
          Voltar
        </Button>
        <Button
          type="submit"
          className="primary px-5"
          disabled={isSubmitting}
          isProcessing={isSubmitting}
        >
          {!isSubmitting ? "Próximo" : "Salvando..."}
        </Button>
      </div>
      {error && (
        <Alert color="failure" withBorderAccent className="mt-4">
          Erro ao tentar salvar o laudo! Tente novamente mais tarde.
        </Alert>
      )}

      <Modal
        show={openModalSearchPlate}
        size="xl"
        onClose={() => setOpenModalSearchPlate(false)}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-2 h-10 w-10 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-2 text-lg font-normal text-gray-500 dark:text-gray-400">
              Ao realizar uma consulta por placa, será <b>contabilizado</b> o
              uso da consulta <b className="text-green-700">Leilão VIP Gold</b>{" "}
              e o resultado da mesma será mostrado no <b>final do laudo</b>.
            </h3>
            <div className="text-sm border border-gray-200 bg-gray-100 rounded-md p-1 mb-5">
              Importante: A consulta pode demorar um pouco, já que reunimos
              todos os dados relevantes do veículo para garantir uma análise
              completa para o laudo.
            </div>
            <div className="flex flex-col md:flex-row justify-center gap-2">
              <Button
                className="primary"
                onClick={getInfoByPlate}
                isProcessing={isGettingVehicleInfos}
                disabled={isGettingVehicleInfos}
              >
                Buscar placa
              </Button>
              <Button
                className="bg-gray-400"
                onClick={() => setOpenModalSearchPlate(false)}
                disabled={isGettingVehicleInfos}
              >
                Cancelar busca
              </Button>
            </div>
            {errorQuery && (
              <Alert className="mt-3" color="failure" withBorderAccent>
                {errorQuery}
              </Alert>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </form>
  );
};

export default SurveyVehicle;
